import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { Person } from "../../../api/client";
import { usePeople, useTeams } from "../../../api/hooks";
import { PersonPanel, statusOf } from "../../../components/admin/PersonPanel";
import { PageHead } from "../../../components/shell/Page";
import { LoadFailed } from "../../../components/ui/LoadState";
import { Avatar, Icon, StatusDot, TeamChip, TeamIcon } from "../../../components/ui/primitives";
import { type Key, t, tn } from "../../../i18n";
import { NARROW_PANEL, useMedia } from "../../../lib/useMedia";

type Search = { id?: string };

export const Route = createFileRoute("/_app/admin/people")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    id: typeof s.id === "string" ? s.id : undefined,
  }),
  component: PeoplePage,
});

const REACH: Record<Person["reach_by"], Key> = {
  web: "admin.reachWeb",
  email: "admin.reachEmail",
  both: "admin.reachBoth",
};

/** A tool account's state, said in a tooltip. */
const ACCOUNT_STATE: Record<Person["accounts"][number]["status"], Key> = {
  linked: "admin.acctLinked",
  missing: "admin.acctMissing",
  not_needed: "admin.notNeeded",
  held: "admin.acctHeld",
};

function Accounts({ p }: { p: Person }) {
  return (
    <span className="accts">
      {(["redmine", "github", "outline"] as const).map((tool) => {
        const acc = p.accounts.find((a) => a.tool === tool);
        const cls =
          acc?.status === "linked" ? "acct ok" : acc?.status === "missing" ? "acct no" : "acct";
        const title = `${tool[0]?.toUpperCase()}${tool.slice(1)} · ${
          acc ? t(ACCOUNT_STATE[acc.status]) : t("admin.acctNotCheckedYet")
        }`;
        return (
          <span key={tool} className={cls} title={title}>
            {tool[0]?.toUpperCase()}
          </span>
        );
      })}
    </span>
  );
}

function PeoplePage() {
  const { id } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const goTo = useNavigate();
  const narrow = useMedia(NARROW_PANEL);
  const people = usePeople("all");
  const teams = useTeams();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const all = people.data ?? [];
  const active = all.filter((p) => p.status !== "deactivated");
  const attention = active.filter((p) => p.accounts.some((a) => a.status === "missing"));
  const deactivated = all.filter((p) => p.status === "deactivated");
  const normalTeams = (teams.data ?? []).filter((t) => t.kind === "team");
  const everyoneTeam = (teams.data ?? []).find((t) => t.kind === "everyone");

  const rows = useMemo(() => {
    let list = filter === "deactivated" ? deactivated : active;
    if (filter === "admins") list = list.filter((p) => p.role === "admin");
    else if (filter === "attention") list = attention;
    else if (filter !== "all" && filter !== "deactivated")
      list = list.filter((p) => p.teams.some((t) => t.id === filter));
    const q = query.trim().toLowerCase();
    if (q)
      list = list.filter((p) =>
        [p.called_name, p.full_name, p.email, p.title, p.about].join(" ").toLowerCase().includes(q),
      );
    return list;
  }, [filter, query, active, attention, deactivated]);

  const selected = id && id !== "new" ? (all.find((p) => p.id === id) ?? null) : null;
  const panelOpen = id === "new" || selected !== null;
  const open = (pid: string | undefined) => navigate({ search: { id: pid } });

  const chips: { key: string; label: React.ReactNode; n: number; warn?: boolean }[] = [
    { key: "all", label: t("admin.filterAll"), n: active.length },
    ...normalTeams.map((t) => ({
      key: t.id,
      label: (
        <>
          <TeamIcon team={t} size="s" />
          {t.name}
        </>
      ),
      n: t.member_count,
    })),
    {
      key: "admins",
      label: t("admin.filterAdmins"),
      n: active.filter((p) => p.role === "admin").length,
    },
  ];
  if (attention.length)
    chips.push({
      key: "attention",
      label: t("admin.needsAttention"),
      n: attention.length,
      warn: true,
    });
  if (deactivated.length)
    chips.push({ key: "deactivated", label: t("admin.deactivated"), n: deactivated.length });

  return (
    <main
      className={panelOpen ? "sub main on" : "sub main on no-panel"}
      aria-label={t("shell.people")}
    >
      <div className="page">
        <PageHead
          title={t("shell.people")}
          subtitle={t("admin.peopleSubtitle", {
            people: tn("admin.peopleCount", active.length),
          })}
          actions={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  // Onboarding by one request (PLAN §8.2, §8.10): a new chat with the ask begun.
                  // Nothing is made until it's sent (R10-S12: no empty "New chat" rows).
                  void goTo({ to: "/", search: { q: t("admin.setupAsk") } })
                }
              >
                <Icon name="i-spark" size="s" />
                {t("admin.setUpWithVantage")}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => open("new")}>
                <Icon name="i-user-plus" size="s" />
                {t("admin.invite")}
              </button>
            </>
          }
        />
        <div className="toolbar">
          <input
            className="tsearch"
            type="search"
            placeholder={t("admin.searchPeopleDots")}
            aria-label={t("admin.searchPeople")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="chip-group">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                className={c.warn ? "fchipx warn" : "fchipx"}
                aria-pressed={filter === c.key}
                onClick={() => setFilter(c.key)}
              >
                {c.warn && <Icon name="i-alert" size="s" />}
                {c.label} <span className="n">{c.n}</span>
              </button>
            ))}
          </span>
        </div>
        <div className="page-body scroll">
          <div className="dtable">
            <div className="tbl-scroll scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("admin.colPersonAbout")}</th>
                    <th className="col-opt">{t("admin.title")}</th>
                    <th>{t("shell.teams")}</th>
                    <th className="accts-cell">{t("admin.toolAccounts")}</th>
                    <th>{t("admin.colStatus")}</th>
                    <th className="col-opt">{t("admin.reachBy")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filter === "all" && !query && (
                    <tr className="agent-row">
                      <td>
                        <div className="person">
                          <span className="av agent">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <use href="#vmark" />
                            </svg>
                          </span>
                          <div>
                            <div className="pname">
                              Vantage <span className="tag-ai">AI</span>
                            </div>
                            <div className="pmeta">{t("shell.tagline")}</div>
                            <div className="pabout">{t("admin.vantageAbout")}</div>
                          </div>
                        </div>
                      </td>
                      <td className="col-opt">{t("admin.vantageTitle")}</td>
                      <td>
                        <TeamChip
                          team={everyoneTeam ?? { icon: "megaphone", hue_token: "team-all" }}
                        >
                          Everyone
                        </TeamChip>
                      </td>
                      <td className="accts-cell">
                        <span className="accts">
                          <span className="acct" title="Redmine">
                            R
                          </span>
                          <span className="acct" title="GitHub">
                            G
                          </span>
                          <span className="acct" title="Outline">
                            O
                          </span>
                        </span>
                      </td>
                      <td>
                        {/* No build-round wording (R10-K9): it works, through its own accounts. */}
                        <StatusDot tone="ok">{t("shell.active")}</StatusDot>
                      </td>
                      <td className="col-opt reach muted">—</td>
                    </tr>
                  )}
                  {rows.map((p) => {
                    const st = statusOf(p);
                    return (
                      <tr
                        key={p.id}
                        className={p.id === id ? "sel" : undefined}
                        data-open-panel
                        tabIndex={0}
                        aria-label={t("admin.openDetails", { name: p.called_name })}
                        onClick={() => open(p.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open(p.id);
                          }
                        }}
                      >
                        <td>
                          <div className="person">
                            <Avatar id={p.id} name={p.called_name} hue={p.avatar_hue} />
                            <div>
                              <div className="pname">{p.called_name}</div>
                              <div className="pmeta">
                                {[
                                  p.full_name !== p.called_name ? p.full_name : null,
                                  p.role === "admin" ? t("shell.roleAdmin") : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || p.email}
                              </div>
                              {p.about && <div className="pabout">{p.about}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="col-opt">{p.title}</td>
                        <td>
                          {p.teams.map((t) => (
                            <TeamChip key={t.id} team={t}>
                              {t.name}
                            </TeamChip>
                          ))}
                        </td>
                        {/* Kept in the cards at 768 and 390 (R10-K17). */}
                        <td className="accts-cell">
                          <Accounts p={p} />
                        </td>
                        <td>
                          <StatusDot tone={st.tone}>{st.text}</StatusDot>
                        </td>
                        <td className="col-opt reach">{t(REACH[p.reach_by])}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {people.isSuccess && rows.length === 0 && (
            <div className="empty">
              <p>{t("shell.noOneMatches")}</p>
            </div>
          )}
          {/* Loading and failing say so, with Retry (R10-S3), not a blank table. */}
          {people.isLoading && (
            <div className="load-state" role="status" aria-busy="true">
              {t("common.loadingWhat", { what: t("admin.whatPeople") })}
            </div>
          )}
          {people.isError && <LoadFailed query={people} what={t("admin.whatPeople")} />}
        </div>
      </div>
      {panelOpen && (
        <PersonPanel
          person={selected}
          teams={teams.data ?? []}
          open={narrow}
          onClose={(createdId) => open(createdId)}
        />
      )}
      {panelOpen && narrow && (
        <div className="scrim on" aria-hidden="true" onClick={() => open(undefined)} />
      )}
    </main>
  );
}
